import React, { useEffect } from 'react';
import { bool, number, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FaUserAlt } from "react-icons/fa";

import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { userDisplayNameAsString } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { AvatarLarge, NamedLink, Page, Logo } from '../../components';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import { socialMediaLinks } from '../../config/configListing';
import { MIN_FLASH_LISTINGS_TO_SHOW_BUTTON } from './PersonalLinkLandingPage.duck';
import css from './PersonalLinkLandingPage.module.css';
import { PERSONALIZED_LINK_STORAGE_KEY } from '../../util/personalizedLink';

// This is the ONLY real entry point for a "personalized link" visit (the
// /@username route). Stamping the artist's id here — and checking it at
// booking-initiation time (ListingPage.duck.js's initiateFlashBooking,
// OrderPanel.js's inquiry submit) — is what drives the zero-commission
// override in lineItems.js's isValidShareCode check. A prior mechanism based
// on a ?sh= query param never actually got wired into this page → profile →
// listing navigation chain, so the override never fired for any listing type.

export const PersonalLinkLandingPageComponent = props => {
  const { scrollingDisabled, listing, listingShowError, flashListingsCount, staticContext } = props;

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const authorId = listing?.author?.id?.uuid;

  useEffect(() => {
    if (typeof window !== 'undefined' && authorId) {
      window.sessionStorage.setItem(PERSONALIZED_LINK_STORAGE_KEY, authorId);
    }
  }, [authorId]);

  if (listingShowError && listingShowError.status === 404) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  if (!listing) {
    return null;
  }

  const { description, title } = listing.attributes;
  const author = listing.author;
  const { displayName, bio } = author?.attributes?.profile || {};
  const handle = listing.attributes.publicData?.url ? `${listing.attributes.publicData.url}` : displayName;
  const artistFirstName = userDisplayNameAsString(author, '');
  const showFlashButton = flashListingsCount >= MIN_FLASH_LISTINGS_TO_SHOW_BUTTON;
  const hasProfileImage = !!author?.profileImage?.id;

  // The personal link's own listing doubles as the artist's portfolio piece.
  // Instead of sending visitors to the profile page's portfolio tab, we take
  // them straight to this listing's details page with `orderOpen=true` — the
  // same query param OrderPanel.js's "Solicitar orçamento" CTA button sets via
  // openOrderModal() — so ModalInMobile opens the TattoInquiryForm ("Request a
  // Quote" questionnaire) immediately on mobile, where it's otherwise hidden
  // behind that button. (The `inquiryModalOpenForListingId` flag used
  // elsewhere in ListingPage opens a different, unrelated "contact the
  // author" modal in SectionAuthorMaybe — not this one.)
  const listingId = listing.id.uuid;
  const listingSlug = createSlug(title || '');
  const portfolioListingParams = { id: listingId, slug: listingSlug };
  const portfolioListingPath = createResourceLocatorString(
    'ListingPage',
    routeConfiguration,
    portfolioListingParams,
    { orderOpen: true }
  );

  const handlePortfolioClick = e => {
    e.preventDefault();
    // Belt-and-braces: the mount-time effect above should have already
    // stamped this, but stamp it again synchronously here so the
    // zero-commission override is guaranteed to be in place before we
    // navigate to the listing page, regardless of effect timing.
    if (typeof window !== 'undefined' && authorId) {
      window.sessionStorage.setItem(PERSONALIZED_LINK_STORAGE_KEY, authorId);
    }
    history.push(portfolioListingPath);
  };

  const redesocial = author?.attributes?.profile?.publicData?.redesocial;
  const socialIcon = (() => {
    if (!redesocial) return null;
    const value = redesocial.trim();
    const lower = value.toLowerCase();

    const platform = lower.includes('instagram')
      ? 'instagram'
      : lower.includes('tiktok')
      ? 'tiktok'
      : null;
    if (!platform) return null;

    const config = socialMediaLinks.find(s => s.key === platform);
    if (!config) return null;

    let href = null;
    if (lower.startsWith('http')) {
      href = value;
    } else if (lower.includes('.com')) {
      href = `https://${value}`;
    }
    // If it's just "Instagram" or "TikTok" with no URL/handle, don't render a broken link
    return href ? { key: platform, href, logo: config.logo, label: config.label } : null;
  })();

  return (
    <Page title={handle} scrollingDisabled={scrollingDisabled}>
      <div className={css.root}>
        <div className={css.logoContainer}>
          <Logo layout="desktop" />
        </div>
        <div className={css.card}>
          {hasProfileImage ? (
            <AvatarLarge className={css.avatar} user={author} disableProfileLink />
          ) : (
            <div className={css.defaultAvatarWrapper}>
              <FaUserAlt />
            </div>
          )}
          {artistFirstName ? <h1 className={css.handle}>{artistFirstName}</h1> : null}
          {bio || description ? <p className={css.bio}>{bio || description}</p> : null}

          <div className={css.actions}>
            <a className={css.actionButton} href={portfolioListingPath} onClick={handlePortfolioClick}>
              <FormattedMessage id="PersonalLinkLandingPage.portfolioButton" />
            </a>
            {showFlashButton ? (
              <NamedLink
                className={css.actionButtonOutline}
                name="ArtistProfilePage"
                params={{ id: authorId, tab: 'flash' }}
              >
                <FormattedMessage id="PersonalLinkLandingPage.flashButton" />
              </NamedLink>
            ) : null}
          </div>

          {/* Instagram/TikTok link hidden per design request; logic kept for future use.
          {socialIcon && (
            <ul className={css.socials}>
              <li className={css.socialItem}>
                <a href={socialIcon.href} target="_blank" rel="noopener noreferrer">
                  <img src={socialIcon.logo} alt={socialIcon.label} className={css.socialIcon} />
                </a>
              </li>
            </ul>
          )} */}
        </div>
      </div>
    </Page>
  );
};

PersonalLinkLandingPageComponent.defaultProps = {
  listing: null,
  listingShowError: null,
  flashListingsCount: 0,
  staticContext: null,
};

PersonalLinkLandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  listingShowError: propTypes.error,
  flashListingsCount: number,
  staticContext: object,
};

const mapStateToProps = state => {
  const { listingRef, listingShowError, flashListingsCount } = state.PersonalLinkLandingPage;
  const listings = listingRef ? getMarketplaceEntities(state, [listingRef]) : [];

  return {
    scrollingDisabled: isScrollingDisabled(state),
    listing: listings.length === 1 ? listings[0] : null,
    listingShowError,
    flashListingsCount,
  };
};

const PersonalLinkLandingPage = compose(connect(mapStateToProps))(PersonalLinkLandingPageComponent);

export default PersonalLinkLandingPage;
